import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  standardColours,
  greyColours,
  brandColours,
  fontWeights,
  fontSize,
  visuallyHidden,
} from '../styles';
import { Svg } from './ui';
import IconContent from './IconContent';
import { formatDate, formatPrice } from '../utils';
import phoneIcon from '../images/phone-icon-alt.svg';

const StyledCalendar = styled.div`
  position: relative;
  padding: 15px 0;
  color: ${brandColours.primary};
  background-color: ${standardColours.white};
`;

const StyledArrow = styled.button`
  position: absolute;
  top: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  height: 28px;
  width: 28px;
  background-color: ${brandColours.primary};
  border-radius: 50%;

  &:disabled {
    opacity: 0.4;
  }

  &:before {
    content: '';
    display: block;
    height: 8px;
    width: 8px;
    border: 2px solid ${standardColours.white};
    border-top: 0;
    border-left: 0;
  }

  span {
    ${visuallyHidden()};
  }

  ${({ alt }) => {
    if (alt) {
      return css`
        right: 20px;

        &:before {
          transform: translateX(-1px) rotate(-45deg);
        }
      `;
    } else {
      return css`
        left: 20px;

        &:before {
          transform: translateX(1px) rotate(135deg);
        }
      `;
    }
  }}
`;

const StyledGroups = styled.div`
  ${({ isSearch }) => {
    if (isSearch) {
      return css`
        ${minBreakpointQuery.large`
          display: grid;
          grid-template-columns: repeat(2, 1fr);
        `}
      `;
    }
  }}

  ${({ isPackagePurchase }) => {
    if (isPackagePurchase) {
      return css`
        ${minBreakpointQuery.small`
          display: grid;
          grid-template-columns: repeat(2, 1fr);
        `}

        ${minBreakpointQuery.smedium`
          display: block;
        `}

        ${minBreakpointQuery.large`
          display: grid;
        `}
      `;
    }
  }}
`;

const StyledGroup = styled.div`
  ${({ isSearch }) => {
    if (isSearch) {
      return css`
        &:last-child {
          display: none;

          ${minBreakpointQuery.large`
            display: block;
          `}
        }
      `;
    }
  }}

  ${({ isPackagePurchase }) => {
    if (isPackagePurchase) {
      return css`
        &:last-child {
          display: none;

          ${minBreakpointQuery.small`
            display: block;
          `}

          ${minBreakpointQuery.smedium`
            display: none;
          `}

          ${minBreakpointQuery.large`
            display: block;
          `}
        }
      `;
    }
  }}
`;

const StyledInner = styled.div`
  margin: 15px 10px;

  ${({ first, last }) => {
    if (first) {
      return css`
        ${minBreakpointQuery.large`
          margin-left: 15px;
        `}
      `;
    } else if (last) {
      return css`
        ${minBreakpointQuery.large`
          margin-right: 15px;
        `}
      `;
    }
  }}
`;

const StyledName = styled.p`
  padding: 0 10px;
  ${fontSize(18)};
  text-align: center;

  ${({ isSearch }) => {
    if (!isSearch) {
      return css`
        padding-bottom: 15px;
        border-bottom: 1px solid ${greyColours.lightPurplishGrey};
      `;
    }
  }}
`;

const commonDaysStyles = () => {
  return css`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
  `;
};

const StyledWeekdays = styled.ul`
  ${commonDaysStyles()};
  margin-bottom: 10px;
`;

const StyledWeekday = styled.li`
  color: ${brandColours.primary};
  ${fontSize(12)};
  text-align: center;
  text-transform: uppercase;
`;

const StyledDays = styled.ul`
  ${commonDaysStyles()};
`;

const StyledDay = styled.li`
  height: 46px;
  text-align: center;
  background-color: ${greyColours.lighterPurplishGrey};
`;

// moved up for styling
const StyledInfo = styled.span`
  display: block;
  margin-top: 2px;
  ${fontSize(11)};

  s {
    display: block;
    color: ${greyColours.grey};
    ${fontSize(9)};
  }

  strong {
    font-weight: ${fontWeights.medium};
  }
`;

const StyledButton = styled.button`
  position: relative;
  padding: 0;
  height: 100%;
  width: 100%;
  line-height: 1.1;

  ${({ status, selected, isSearch }) => {
    if (status === 'Available' && selected) {
      return css`
        color: ${brandColours.primary};
        background-color: ${brandColours.secondary};
      `;
    } else if ((status === 'Available' || status === 'Call') && !isSearch) {
      return css`
        background-color: ${standardColours.white};
        border: 1px solid ${greyColours.lightGrey};
      `;
    } else if (status === 'Unavailable') {
      return css`
        color: ${greyColours.purplishGrey};
      `;
    }
  }}

  ${({ status, isSearch }) => {
    if (status === 'Call' && !isSearch) {
      return css`
        position: relative;

        &:before,
        &:after {
          position: absolute;
          left: 50%;
          z-index: 1;
          display: none;
        }

        &:before {
          content: '';
          bottom: 0;
          height: 0;
          width: 0;
          border-left: 8px solid ${standardColours.transparent};
          border-right: 8px solid ${standardColours.transparent};
          border-bottom: 8px solid ${brandColours.primary};
          transform: translate(-50%, 4px);
        }

        &:after {
          content: attr(data-label);
          bottom: 0;
          padding: 10px;
          width: 170px;
          color: ${standardColours.white};
          font-weight: ${fontWeights.medium};
          ${fontSize(12)};
          text-align: center;
          background-color: ${brandColours.primary};
          box-shadow: 0 6px 12px ${brandColours.transparentPrimary(0.25)};
          transform: translate(-50%, calc(100% + 2px));
        }

        &:hover {
          &:before,
          &:after {
            display: block;
          }
        }
      `;
    }
  }}
  
  ${({ band, selected }) => {
    if (band) {
      let colour;

      switch (band) {
        case 1:
          colour = standardColours.green;
          break;
        case 2:
          colour = standardColours.amber;
          break;
        case 3:
          colour = standardColours.red;
          break;
      }

      if (!selected) {
        return css`
          ${StyledInfo} {
            strong {
              color: ${colour};
            }
          }
        `;
      }
    }
  }}
  
  ${({ isSearch }) => {
    if (isSearch) {
      return css`
        min-width: 40px;

        ${minBreakpointQuery.small`
          min-width: 42px;
        `}

        ${minBreakpointQuery.medium`
          min-width: 44px;
        `}

        ${minBreakpointQuery.large`
          min-width: 46px;
        `}
      `;
    } else {
      return css`
        ${fontSize(14)};
      `;
    }
  }}
`;

const StyledPhoneIcon = styled(Svg)`
  position: absolute;
  top: 5px;
  right: 5px;
  height: 9px;
  width: 4px;
  fill: ${brandColours.primary};
`;

const Calendar = ({
  months = [],
  startDate,
  setStartDate,
  selectedDate,
  setSelectedDate,
  isSearch,
  isPackagePurchase,
  children,
  ...props
}) => {
  const {
    spaseekers: {
      site: { telephone },
    },
  } = useStaticQuery(graphql`
    query CalendarQuery {
      spaseekers {
        site {
          telephone
        }
      }
    }
  `);

  const hasMonths = months.length > 0;

  const weekdays = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];

  const todaysDate = new Date();

  const formatMonthYear = date => {
    const formattedDate = `${date.getMonth()}-${date.getYear()}`;

    return formattedDate;
  };

  const selectDate = date => {
    setSelectedDate(date);
  };

  return (
    <StyledCalendar {...props}>
      <StyledArrow
        type="button"
        {...(hasMonths &&
        formatMonthYear(startDate) !== formatMonthYear(todaysDate)
          ? {
              onClick: () =>
                setStartDate(
                  new Date(startDate.setMonth(startDate.getMonth() - 1)),
                ),
            }
          : {
              disabled: true,
            })}
      >
        <span>Previous Month</span>
      </StyledArrow>
      {hasMonths ? (
        <StyledGroups isSearch={isSearch} isPackagePurchase={isPackagePurchase}>
          {months.map(({ month, dates }, i) => {
            const firstDate = new Date(dates[0].date);
            const dayGapsBefore =
              firstDate.getDay() === 0 ? 6 : firstDate.getDay() - 1;
            const datesGapsBefore = dates.length + dayGapsBefore;
            const dayGapsAfter = !Number.isInteger(datesGapsBefore / 7)
              ? Math.ceil(datesGapsBefore / 7) * 7 - datesGapsBefore
              : 0;

            return (
              <StyledGroup
                isSearch={isSearch}
                isPackagePurchase={isPackagePurchase}
              >
                <StyledName isSearch={isSearch}>{month}</StyledName>
                <StyledInner first={i === 0} last={i === months.length - 1}>
                  <StyledWeekdays>
                    {weekdays.map((weekday, id) => (
                      <StyledWeekday key={id} aria-label={weekday}>
                        <span aria-hidden="true">{weekday.slice(0, 3)}</span>
                      </StyledWeekday>
                    ))}
                  </StyledWeekdays>
                  <StyledDays>
                    {dayGapsBefore > 0 &&
                      [...Array(dayGapsBefore)].map(id => (
                        <StyledDay key={id}></StyledDay>
                      ))}
                    {dates.map((date, id) => {
                      const day = new Date(date.date).getDate();

                      if (selectedDate && selectedDate.date === date.date) {
                        setSelectedDate(date.status === 'Available' && date);
                      }

                      return (
                        <StyledDay key={id}>
                          <StyledButton
                            type="button"
                            {...(date.status === 'Available'
                              ? {
                                  title: `Select ${formatDate(date.date)}`,
                                  'aria-label': `Select ${formatDate(
                                    date.date,
                                  )}`,
                                }
                              : date.status === 'Call'
                                ? {
                                    'aria-label': `Limited Availaiblity on this date: Call ${telephone} for more Information.`,
                                  }
                                : '')}
                            {...(date.status === 'Call'
                              ? {
                                  'data-label': `Limited Availaiblity on this date: Call ${telephone} for more Information.`,
                                }
                              : '')}
                            status={date.status}
                            band={date.band}
                            selected={
                              selectedDate && date.date === selectedDate.date
                            }
                            isSearch={isSearch}
                            {...(date.status === 'Available'
                              ? {
                                  onClick: () => selectDate(date),
                                }
                              : {
                                  disabled: true,
                                })}
                          >
                            {date.status === 'Call' && (
                              <StyledPhoneIcon image={phoneIcon} />
                            )}
                            {day}
                            {(date.status === 'Available' ||
                              date.status === 'Call') &&
                              !isSearch && (
                                <StyledInfo>
                                  {date.status === 'Available' ? (
                                    <>
                                      {date.higherPrice && (
                                        <s>
                                          {formatPrice(date.higherPrice, true)}
                                        </s>
                                      )}
                                      <strong>
                                        {formatPrice(date.price, true)}
                                      </strong>
                                    </>
                                  ) : (
                                    'Limited'
                                  )}
                                </StyledInfo>
                              )}
                          </StyledButton>
                        </StyledDay>
                      );
                    })}
                    {dayGapsAfter > 0 &&
                      [...Array(dayGapsAfter)].map(id => (
                        <StyledDay key={id}></StyledDay>
                      ))}
                  </StyledDays>
                </StyledInner>
              </StyledGroup>
            );
          })}
        </StyledGroups>
      ) : (
        <IconContent heading="Seeking availability…" />
      )}
      <StyledArrow
        type="button"
        alt={true}
        {...(hasMonths
          ? {
              onClick: () => {
                const date = new Date(
                  startDate.setMonth(startDate.getMonth() + 1, 1),
                );
                setStartDate(date);
              },
            }
          : {
              disabled: true,
            })}
      >
        <span>Next Month</span>
      </StyledArrow>
      {children}
    </StyledCalendar>
  );
};

export default Calendar;
